/**
 * NOMBRAR A UN COMPAÑERO CON @ — LA PARTE QUE NO SABE DE REACT.
 *
 * Lissette, 2026-09-12: *"¿podemos crear el sistema que cuando se mencione en un
 * comentario, notas, a uno del equipo le llegue la notificación así
 * personalizada en el chat, como le llega cuando le asignan un ticket o
 * tarea?"*. Y a la pregunta de dónde: **«Todos los hilos que haya»**.
 *
 * ── LO QUE ESTE ARCHIVO NO HACE ────────────────────────────────────────────
 *
 * No decide a quién se avisa. Eso lo hace el SERVIDOR
 * (`deskte-api/src/utils/menciones.ts`), buscando los nombres del equipo dentro
 * del texto ya guardado. Aquí solo está el desplegable que ayuda a escribir la
 * mención bien: se teclea `@ma`, se elige de la lista y queda `@María Fernanda`
 * escrito tal cual en la nota.
 *
 * Eso significa que **una mención escrita a mano funciona igual**, en cualquier
 * caja de texto, con o sin desplegable. La pantalla es una comodidad, no el
 * mecanismo — y por eso la función llegó entera a todos los hilos desde el
 * primer día, sin tener que tocar diez composers a la vez.
 *
 * ── POR QUÉ EL TEXTO QUEDA LIMPIO ──────────────────────────────────────────
 *
 * Se guarda `@María Fernanda`, no `@[María Fernanda](id)`. El texto de una nota
 * es PHI, se guarda cifrado y se vuelve a leer en muchos sitios que no sabrían
 * deshacer una marca así —el CSV, un correo, el portal del cliente— y se vería
 * el corchetazo en crudo. Ver la cabecera del gemelo del servidor.
 */

/** Minúsculas y sin tildes, para que `@ma` encuentre a "María". */
export function normalizar(s) {
  return String(s || '').toLowerCase().normalize('NFD').replace(/\p{M}/gu, '');
}

/**
 * ¿Está el cursor escribiendo una mención? Devuelve `{ desde, consulta }` o
 * `null`.
 *
 * `desde` es la posición de la arroba, que es lo que luego hay que reemplazar.
 *
 * Tres frenos, y los tres son casos que de verdad pasan:
 *
 *   1. **Delante de la arroba no puede haber letra ni número.** Si no, escribir
 *      un correo (`soporte@ambetter.com`) abriría el desplegable a media
 *      palabra.
 *   2. **Un salto de línea corta.** Una arroba de hace tres párrafos no sigue
 *      "abierta".
 *   3. **Como mucho tres palabras.** Los nombres del equipo no son más largos,
 *      y sin tope una arroba suelta al principio de la nota dejaría el menú
 *      abierto durante toda la frase.
 */
export function consultaDeMencion(texto, cursor) {
  const s = String(texto || '');
  const pos = Math.max(0, Math.min(Number(cursor) || 0, s.length));
  for (let i = pos - 1; i >= 0 && pos - i <= 40; i--) {
    const ch = s[i];
    if (ch === '\n') return null;                                    // freno 2
    if (ch !== '@') continue;
    if (/[\p{L}\p{N}]/u.test(s[i - 1] || '')) return null;           // freno 1
    const consulta = s.slice(i + 1, pos);
    if (consulta.trim().split(/\s+/).filter(Boolean).length > 3) return null; // freno 3
    return { desde: i, consulta };
  }
  return null;
}

/**
 * Los compañeros que encajan con lo tecleado, los mejores primero.
 *
 * Ordena por DÓNDE encaja: quien empieza por lo que se escribió va antes que
 * quien lo lleva por el medio. Tecleando "ma", "María" tiene que salir por
 * encima de "Ana María".
 */
export function filtrarCompaneros(usuarios, consulta, tope = 6) {
  const lista = (Array.isArray(usuarios) ? usuarios : [])
    .map((u) => ({ id: u?.id, nombre: String(u?.name || u?.displayName || '').trim(), email: u?.email || '' }))
    // Sin nombre no se puede nombrar a nadie: el servidor busca por
    // `displayName`, y un usuario sin él no aparece en su lista tampoco. Ofrecer
    // aquí un correo sería ofrecer una mención que nunca llega.
    .filter((u) => u.id && u.nombre && u.nombre !== u.email);
  const q = normalizar(consulta).trim();
  if (!q) return lista.slice(0, tope);
  const puntuado = [];
  for (const u of lista) {
    const donde = normalizar(u.nombre).indexOf(q);
    if (donde === -1) continue;
    puntuado.push({ ...u, donde });
  }
  puntuado.sort((a, b) => a.donde - b.donde || a.nombre.localeCompare(b.nombre));
  return puntuado.slice(0, tope).map(({ donde, ...u }) => u); // eslint-disable-line no-unused-vars
}

/**
 * Mete el nombre elegido en el texto y dice dónde queda el cursor.
 *
 * Deja un espacio detrás a propósito: sin él, lo siguiente que se escriba se
 * pega al nombre y el servidor ya no lo reconoce (su regla es que detrás de un
 * nombre nombrado se acaba la palabra, justo para que `@Ana` no salte dentro de
 * `@Anabel`).
 */
export function insertarMencion(texto, desde, cursor, nombre) {
  const s = String(texto || '');
  const limpio = String(nombre || '').trim();
  if (!limpio) return { texto: s, cursor };
  const izquierda = s.slice(0, desde);
  const derecha = s.slice(Math.max(desde, Number(cursor) || 0));
  const pegado = `@${limpio} `;
  // Si ya había un espacio justo detrás, no se ponen dos.
  const cola = derecha.startsWith(' ') ? derecha.slice(1) : derecha;
  return { texto: izquierda + pegado + cola, cursor: izquierda.length + pegado.length };
}
